import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import backendVerifiedUser from '../../api/backendVerifiedUser';

export default function GSCPlugin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({
    connected: false,
    is_active: false,
    site_url: '',
    client_id: '',
    redirect_uri: '',
    admin_panel_url: '',
    client_secret_set: false,
  });

  // Step 1 form
  const [form, setForm] = useState({
    client_id: '',
    client_secret: '',
    redirect_uri: '',
    admin_panel_url: '',
  });
  const [savingCreds, setSavingCreds] = useState(false);

  // Step 3 — site selection
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [loadingSites, setLoadingSites] = useState(false);
  const [savingSite, setSavingSite] = useState(false);

  // Step 4 — search analytics
  const [report, setReport] = useState(null);
  const [days, setDays] = useState(28);
  const [loadingReport, setLoadingReport] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const DAY_RANGES = [
    { label: '7 days', value: 7 },
    { label: '28 days', value: 28 },
    { label: '90 days', value: 90 },
  ];

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await backendVerifiedUser.get('/admin/plugins/gsc/status');
      const d = res.data;
      setStatus(d);
      if (d.client_id) {
        setForm((f) => ({
          ...f,
          client_id: d.client_id,
          redirect_uri: d.redirect_uri || '',
          admin_panel_url: d.admin_panel_url || '',
        }));
      }
      if (d.site_url) setSelectedSite(d.site_url);
    } catch {
      toast.error('Failed to load plugin status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReport = useCallback(async (range) => {
    setLoadingReport(true);
    try {
      const res = await backendVerifiedUser.get(`/admin/plugins/gsc/report?days=${range || days}`);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to load search data');
    } finally {
      setLoadingReport(false);
    }
  }, [days]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Handle OAuth redirect back
  useEffect(() => {
    const connected = searchParams.get('connected');
    const err = searchParams.get('error');
    if (connected === 'true') {
      toast.success('Google account connected!');
      fetchStatus();
    } else if (err) {
      const msgs = {
        no_code: 'Authorization cancelled.',
        auth_failed: 'Authorization failed. Check your credentials.',
        not_configured: 'Save your credentials first, then try again.',
        plugin_not_found: 'Plugin not found in database.',
      };
      toast.error(msgs[err] || 'Connection failed.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto load report when fully active
  useEffect(() => {
    if (status.connected && status.site_url && status.is_active) {
      fetchReport(days);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.connected, status.site_url, status.is_active]);

  const handleSaveCreds = async () => {
    if (!form.client_id || !form.client_secret || !form.redirect_uri) {
      toast.error('Client ID, Client Secret and Redirect URI are required');
      return;
    }
    setSavingCreds(true);
    try {
      await backendVerifiedUser.post('/admin/plugins/gsc/save-credentials', form);
      toast.success('Credentials saved successfully');
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await backendVerifiedUser.get('/admin/plugins/gsc/auth-url');
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to start authorization');
      setConnecting(false);
    }
  };

  const handleLoadSites = async () => {
    setLoadingSites(true);
    setSites([]);
    try {
      const res = await backendVerifiedUser.get('/admin/plugins/gsc/sites');
      const list = res.data.sites || [];
      setSites(list);
      if (!list.length) toast.info('No verified Search Console properties found in this Google account.');
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to load sites');
    } finally {
      setLoadingSites(false);
    }
  };

  const handleSaveSite = async () => {
    if (!selectedSite) { toast.error('Please select a site'); return; }
    setSavingSite(true);
    try {
      await backendVerifiedUser.post('/admin/plugins/gsc/save-site', { site_url: selectedSite });
      toast.success('Site saved!');
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to save site');
    } finally {
      setSavingSite(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google account? Your credentials will remain saved.')) return;
    setDisconnecting(true);
    try {
      await backendVerifiedUser.post('/admin/plugins/gsc/disconnect');
      toast.success('Disconnected successfully');
      setReport(null);
      setSites([]);
      await fetchStatus();
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const credsSaved = !!status.client_id;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="mx-3 pb-10 max-w-2xl">

      {/* Header */}
      <div className="flex items-center gap-3 mb-7">
        <button
          onClick={() => navigate('/plugins')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
          <svg viewBox="0 0 24 24" className="w-6 h-6">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-800">Google Search Console</h1>
          <p className="text-sm text-gray-400">Track search performance inside admin panel</p>
        </div>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${status.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {status.is_active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="space-y-4">

        {/* ─── STEP 1: Google Cloud Setup Guide ─── */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
          <p className="text-sm font-semibold text-blue-800 mb-2">How to get credentials from Google Cloud</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
            <li>Go to <strong>console.cloud.google.com</strong> → create a new project</li>
            <li>Enable <strong>Google Search Console API</strong></li>
            <li>Go to <strong>APIs & Services → Credentials</strong> → Create OAuth 2.0 Client ID</li>
            <li>Application type: <strong>Web application</strong></li>
            <li>Add your backend callback URL in <strong>Authorized redirect URIs</strong></li>
            <li>Copy <strong>Client ID</strong> and <strong>Client Secret</strong> from the created credential</li>
            <li>Make sure your Google account is a <strong>verified owner/user</strong> of the property in Search Console</li>
          </ol>
        </div>

        {/* ─── STEP 2: Enter Credentials ─── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-6 h-6 rounded-full bg-gray-800 text-white text-xs flex items-center justify-center font-bold">1</span>
            <h2 className="text-base font-semibold text-gray-800">Enter OAuth Credentials</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Client ID <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="123456789-abcdef.apps.googleusercontent.com"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Client Secret <span className="text-red-500">*</span>
                {status.client_secret_set && <span className="ml-2 text-xs text-green-600 font-normal">(already saved)</span>}
              </label>
              <input
                type="password"
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder={status.client_secret_set ? '••••••••••••••• (enter new to change)' : 'GOCSPX-••••••••••••'}
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Redirect URI <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.redirect_uri}
                onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })}
                placeholder="https://your-backend.com/admin/plugins/gsc/callback"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">This exact URL must be added in Google Cloud → Authorized redirect URIs</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Panel URL</label>
              <input
                type="text"
                value={form.admin_panel_url}
                onChange={(e) => setForm({ ...form, admin_panel_url: e.target.value })}
                placeholder="https://your-admin-panel.com"
                className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">Google will redirect here after authorization</p>
            </div>

            <button
              onClick={handleSaveCreds}
              disabled={savingCreds}
              className="w-full py-2.5 bg-gray-800 hover:bg-gray-900 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {savingCreds ? 'Saving...' : credsSaved ? 'Update Credentials' : 'Save Credentials'}
            </button>
          </div>
        </div>

        {/* ─── STEP 3: Connect Google Account ─── */}
        {credsSaved && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-gray-800 text-white text-xs flex items-center justify-center font-bold">2</span>
              <h2 className="text-base font-semibold text-gray-800">Connect Google Account</h2>
            </div>

            {status.connected ? (
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-green-800">Google Account Connected</p>
                    <p className="text-xs text-green-600">Authorization is active and working</p>
                  </div>
                </div>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                >
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">Click the button below — you will be taken to Google's secure login page to authorize access.</p>
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="flex items-center gap-3 px-5 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all disabled:opacity-50"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 shrink-0">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  {connecting ? 'Redirecting to Google...' : 'Sign in with Google'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 4: Select Search Console Site ─── */}
        {status.connected && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 rounded-full bg-gray-800 text-white text-xs flex items-center justify-center font-bold">3</span>
              <h2 className="text-base font-semibold text-gray-800">Select Search Console Property</h2>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-500">Load verified properties linked to your Google account and select your website.</p>

              <button
                onClick={handleLoadSites}
                disabled={loadingSites}
                className="px-4 py-2.5 border border-gray-200 text-sm font-medium text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {loadingSites ? 'Loading properties...' : 'Load My Properties'}
              </button>

              {sites.length > 0 && (
                <>
                  <select
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select a property --</option>
                    {sites.map((s) => (
                      <option key={s.siteUrl} value={s.siteUrl}>{s.siteUrl} ({s.permissionLevel})</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSaveSite}
                    disabled={savingSite || !selectedSite}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                  >
                    {savingSite ? 'Saving...' : 'Save & View Search Data'}
                  </button>
                </>
              )}

              {status.site_url && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-xl">
                  <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-xs text-blue-700">Active property: <strong>{status.site_url}</strong></span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── SEARCH PERFORMANCE DASHBOARD ─── */}
        {status.connected && status.site_url && status.is_active && (
          <div className="space-y-4">
            {/* Date range tabs */}
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">Search Performance</h2>
              <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
                {DAY_RANGES.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => { setDays(d.value); fetchReport(d.value); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${days === d.value ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {loadingReport ? (
              <div className="flex items-center justify-center py-14 bg-white rounded-2xl border border-gray-100">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
              </div>
            ) : report ? (
              <>
                {/* KPI row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Clicks', value: Number(report.overview.clicks).toLocaleString(), icon: '🖱️' },
                    { label: 'Impressions', value: Number(report.overview.impressions).toLocaleString(), icon: '👁️' },
                    { label: 'Avg. CTR', value: report.overview.ctr, icon: '📈' },
                    { label: 'Avg. Position', value: report.overview.position, icon: '🎯' },
                  ].map((k) => (
                    <div key={k.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                      <span className="text-xl">{k.icon}</span>
                      <div>
                        <p className="text-xs text-gray-400">{k.label}</p>
                        <p className="text-lg font-bold text-gray-800">{k.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Top Queries */}
                {report.topQueries?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Search Queries</h3>
                    <div className="space-y-2">
                      {report.topQueries.map((q, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-bold text-gray-300 w-4 shrink-0">{i + 1}</span>
                            <span className="text-gray-700 truncate">{q.query}</span>
                          </div>
                          <div className="flex gap-3 shrink-0 ml-2 text-xs text-gray-400">
                            <span>{Number(q.clicks).toLocaleString()} clicks</span>
                            <span>{Number(q.impressions).toLocaleString()} impr.</span>
                            <span>{q.ctr} CTR</span>
                            <span>pos {q.position}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Pages */}
                {report.topPages?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Pages</h3>
                    <div className="space-y-2">
                      {report.topPages.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-bold text-gray-300 w-4 shrink-0">{i + 1}</span>
                            <span className="text-gray-700 truncate">{p.page}</span>
                          </div>
                          <div className="flex gap-3 shrink-0 ml-2 text-xs text-gray-400">
                            <span>{Number(p.clicks).toLocaleString()} clicks</span>
                            <span>{Number(p.impressions).toLocaleString()} impr.</span>
                            <span>{p.ctr} CTR</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => fetchReport(days)}
                  className="w-full py-2.5 border border-gray-200 text-sm font-medium text-gray-500 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Refresh Data
                </button>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
